import type * as React from "react"
import { useTranslation } from "react-i18next"
import { ActionTiles } from "@/features/home/ActionTiles"
import { RecentDocuments } from "@/features/home/RecentDocuments"
import { UnbilledDeliveries } from "@/features/home/UnbilledDeliveries"
import { UnpaidBills } from "@/features/payments/UnpaidBills"
import { ChartBilledByMonth } from "@/components/chart-billed-by-month"
import { SectionCards } from "@/components/section-cards"
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert"
import { Button } from "@/components/ui/button"
import { useDashboardSummary } from "@/features/home/use-dashboard-summary"
import { ApiError } from "@/lib/api-error"

/**
 * Home — `dashboard-01`'s body, reordered to §11.2 and wired to the real API.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * TWO DEPARTURES FROM THE BLOCK, BOTH BECAUSE §11.2 SAYS SO IN WORDS.
 *
 * 1. THE ACTION TILES COME FIRST. §11.2: "Four action tiles (LARGEST ELEMENT,
 *    above the fold on a phone)", then the at-a-glance figures, then the recent
 *    documents. The block leads with statistics, which is the exact template
 *    §1.1 rules out — "an admin-dashboard template would put a row of statistics
 *    here and push the buttons under the fold". The owner opens this app to MAKE
 *    something, so the making of it is the first thing under his thumb.
 *
 * 2. THERE IS EXACTLY ONE CHART, AND IT SITS BELOW THE FIGURES. §11.2 originally
 *    said "no charts in v1", and its 2026-08-14 amendment grants one: billed by
 *    month, across the financial year, EVERY COLUMN TAPPABLE. The condition is
 *    the same sentence that used to forbid it — "if a number cannot be acted on,
 *    it does not belong on this screen" — so the chart is a month picker that
 *    happens to show shape, not a statistic. The block's own
 *    `ChartAreaInteractive` is not used: it plots invented daily visitor data and
 *    carries a 7d/30d/90d toggle that means nothing here.
 *
 *    It stays BELOW the tiles and the figures. Reading the year is not why the
 *    owner opened the app.
 *
 * Everything else is the block: the same `SectionCards` container queries, the
 * same spacing rhythm, the same card treatment.
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * ONE REQUEST FEEDS THE WHOLE SCREEN, and only the figures depend on it. The
 * tiles are LINKS — they render immediately and work with no signal at all, so a
 * workshop with one bar still gets a working New Bill button while the summary
 * is loading or failing. Only the cards and the recent list show its state.
 */
export function HomeScreen(): React.JSX.Element {
  const { t } = useTranslation()
  const query = useDashboardSummary()

  return (
    <div className="flex flex-col gap-4 py-4 md:gap-6 md:py-6">
      <ActionTiles lastBillId={query.data?.lastBill?.id ?? null} />

      {query.isError ? (
        <div className="px-4 lg:px-6">
          <Alert variant="destructive">
            <AlertTitle>{t("err.couldNotLoad")}</AlertTitle>
            <AlertDescription className="flex flex-col items-start gap-3">
              {/* The server's own sentence when it has one — it is the only
                * thing that can say WHY, and §11.1 wants plain language. */}
              <span>
                {query.error instanceof ApiError
                  ? query.error.message
                  : t("err.somethingWentWrong")}
              </span>
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  void query.refetch()
                }}
              >
                {t("btn.retry")}
              </Button>
            </AlertDescription>
          </Alert>
        </div>
      ) : (
        <>
          <SectionCards summary={query.data} loading={query.isPending} />
          {/*
            * §17.5's "who owes me money" — the figure §11.2 called the single
            * most useful one a business owner can have, and the one it forbade
            * until payment tracking existed. FIRST of the two conditional lines,
            * above §17.7's count, because money outstanding is the more
            * consequential of the two things waiting on him.
            *
            * It renders NOTHING when the module is off, when the caller lacks
            * `payment.view`, or when nothing is actually owed (the API omits the
            * key in all three cases) — so on a settled day this row does not
            * exist and the screen is unchanged. NO CHART: §11.2 allows exactly
            * one and `ChartBilledByMonth` below is it.
            */}
          <UnpaidBills summary={query.data} />
          {/*
            * §17.7's count, directly beneath the at-a-glance figures and above
            * the chart — a figure, in the figures. It renders NOTHING unless
            * something is actually waiting to be billed (see the component), so
            * on most days this row does not exist and the screen is unchanged.
            */}
          <UnbilledDeliveries summary={query.data} />
          <ChartBilledByMonth summary={query.data} loading={query.isPending} />
        </>
      )}

      <RecentDocuments
        documents={query.data?.recentDocuments ?? []}
        loading={query.isPending}
      />
    </div>
  )
}
